import React, { useMemo, useState } from 'react';
import {
  Callout, ConfirmDialog, Field, FormRow, FormSection, ImageDropzone, PhoneFrame, SaveBar,
  SummaryStrip, Toolbar, formatThaiDate, ghostBtnCls, inputCls, primaryBtnCls, secondaryBtnCls,
  useToast, type ImageValue, type SummaryStat,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  BANNERS, BANNER_STATUS_LABEL, BANNER_VISIBLE_LIMIT, TIERS,
  type Banner, type BannerStatus, type TierId,
} from './loyaltyData';

/**
 * BannerScreen — แบนเนอร์แคมเปญบนหน้าแรกของแอป (FR-HOME-06)
 *
 * แบนเนอร์เป็นพื้นที่ที่ทุกฝ่ายอยากได้ และปัญหาที่เกิดจริงไม่ใช่การอัปโหลดภาพ
 * แต่คือ **ลำดับ** กับ **ใครเห็นอะไร** ซึ่งสองเรื่องนี้มองไม่เห็นจากตาราง
 *
 * จึงทำสองอย่าง:
 *
 *   1. **ลำดับเป็นรายการที่เลื่อนได้ ไม่ใช่ช่องกรอกเลข** — การกรอก "ลำดับ 3" ในตาราง
 *      ทำให้เกิดลำดับซ้ำและช่องว่างทันทีที่มีคนกรอกพร้อมกัน รายการที่เลื่อนขึ้นลง
 *      รับประกันว่าลำดับต่อเนื่องและไม่ซ้ำเสมอ
 *
 *   2. **พรีวิวมีตัวเลือก "ดูอย่างสมาชิกระดับ..."** — แบนเนอร์กำหนดกลุ่มผู้รับได้
 *      ถ้าพรีวิวแสดงทุกใบเสมอ คนตั้งค่าจะไม่เห็นว่าสมาชิก Silver เห็นแค่สองใบ
 *      และใบที่ตั้งใจให้เป็นใบแรกของ Silver จริง ๆ อยู่ลำดับสี่
 *
 * นอกจากนั้นหน้านี้เตือนเมื่อมีแบนเนอร์ที่แสดงอยู่เกิน 5 ใบ
 * เพราะจากอัตราการกดของใบท้าย ๆ ลูกค้าเลื่อนไม่ถึง — เพิ่มใบที่หกไม่ได้เพิ่มการมองเห็น
 * แต่ทำให้ใบที่หนึ่งถึงห้าถูกเห็นน้อยลง
 */

type ScreenState = 'ready' | 'loading' | 'error';

const STATUS_STYLE: Record<BannerStatus, { bg: string; fg: string; icon: string }> = {
  draft: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'edit_note' },
  scheduled: { bg: 'var(--info-bg)', fg: 'var(--info-text)', icon: 'schedule_send' },
  live: { bg: 'var(--success-bg)', fg: 'var(--success-text)', icon: 'play_circle' },
  ended: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'stop_circle' },
};

const IMAGE_SPEC = {
  aspect: '16:9' as const,
  minWidth: 1_440,
  maxKB: 500,
  usedAt: 'แบนเนอร์เลื่อนบนหน้าแรกของแอป กว้างเต็มจอ 390px — ภาพถูกย่อลงทั้งใบ ไม่ครอป ข้อความในภาพต้องอ่านได้ที่ความกว้างนั้น',
};

const ctr = (b: Banner) => (b.impressions === 0 ? null : (b.taps / b.impressions) * 100);

const audienceText = (b: Banner): string => {
  const parts: string[] = [];
  if (b.audience.tiers.length === 0) parts.push('สมาชิกทุกระดับ');
  else parts.push(b.audience.tiers.map((t) => TIERS.find((x) => x.id === t)?.name).join(' · '));
  if (b.audience.segment) parts.push(b.audience.segment);
  return parts.join(' · ');
};

export interface BannerScreenProps {
  initialState?: ScreenState;
}

export const BannerScreen: React.FC<BannerScreenProps> = ({ initialState = 'ready' }) => {
  const toast = useToast();
  const [screenState] = useState<ScreenState>(initialState);
  const [saved, setSaved] = useState<Banner[]>(BANNERS);
  const [draft, setDraft] = useState<Banner[]>(BANNERS);
  const [edit, setEdit] = useState<Banner | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewAs, setPreviewAs] = useState<'all' | TierId>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | BannerStatus>('all');

  const ordered = useMemo(() => [...draft].sort((a, b) => a.order - b.order), [draft]);
  const shown = useMemo(() => ordered.filter((b) => statusFilter === 'all' || b.status === statusFilter), [ordered, statusFilter]);

  const move = (id: string, dir: -1 | 1) => {
    const list = [...ordered];
    const i = list.findIndex((b) => b.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j]!, list[i]!];
    setDraft(list.map((b, k) => ({ ...b, order: k + 1 })));
  };

  const patch = (id: string, next: Partial<Banner>) =>
    setDraft((bs) => bs.map((b) => (b.id === id ? { ...b, ...next } : b)));

  /* --------------------------------------------------------- พรีวิวหน้าแรก */

  const previewList = useMemo(() => ordered.filter((b) => {
    if (b.status !== 'live') return false;
    if (previewAs === 'all') return true;
    return b.audience.tiers.length === 0 || b.audience.tiers.includes(previewAs);
  }), [ordered, previewAs]);

  const liveCount = ordered.filter((b) => b.status === 'live').length;
  const overLimit = liveCount > BANNER_VISIBLE_LIMIT;
  const missingImage = draft.filter((b) => (b.status === 'live' || b.status === 'scheduled') && !b.imageUrl).length;
  const missingAlt = draft.some((b) => b.imageUrl && !b.imageAlt.trim());

  /* ------------------------------------------------------- สิ่งที่เปลี่ยนไป */

  const changes = useMemo(() => {
    const out: Array<{ field: string; from: string; to: string }> = [];
    draft.forEach((b) => {
      const s = saved.find((x) => x.id === b.id);
      if (!s) { out.push({ field: `แบนเนอร์ใหม่ — ${b.title}`, from: 'ไม่มี', to: `ลำดับ ${b.order}` }); return; }
      if (s.order !== b.order) out.push({ field: `ลำดับ — ${b.title}`, from: `${s.order}`, to: `${b.order}` });
      if (s.status !== b.status) out.push({ field: `สถานะ — ${b.title}`, from: BANNER_STATUS_LABEL[s.status], to: BANNER_STATUS_LABEL[b.status] });
      if (s.title !== b.title) out.push({ field: 'ชื่อแบนเนอร์', from: s.title, to: b.title });
      if (s.from !== b.from || s.to !== b.to) {
        out.push({ field: `ช่วงเวลา — ${b.title}`, from: `${formatThaiDate(s.from)}–${formatThaiDate(s.to)}`, to: `${formatThaiDate(b.from)}–${formatThaiDate(b.to)}` });
      }
      if (audienceText(s) !== audienceText(b)) out.push({ field: `กลุ่มผู้รับ — ${b.title}`, from: audienceText(s), to: audienceText(b) });
      if (s.imageUrl !== b.imageUrl) out.push({ field: `ภาพ — ${b.title}`, from: s.imageUrl ? 'ภาพเดิม' : 'ไม่มีภาพ', to: b.imageUrl ? 'ภาพใหม่' : 'เอาออก' });
      if (s.linkTo !== b.linkTo) out.push({ field: `ปลายทางเมื่อกด — ${b.title}`, from: s.linkTo, to: b.linkTo });
    });
    return out;
  }, [draft, saved]);

  const doSave = (reason?: string) => {
    const before = saved;
    setSaved(draft);
    setConfirmOpen(false);
    toast.show({
      tone: 'success',
      message: 'บันทึกแบนเนอร์แล้ว — ลำดับใหม่ขึ้นบนหน้าแรกของแอปทันที · บันทึกใน Audit Log',
      undo: { onUndo: () => { setSaved(before); setDraft(before); }, seconds: 10 },
    });
    void reason;
  };

  const stats: SummaryStat[] = [
    { label: 'แสดงอยู่บนหน้าแรก', value: liveCount.toLocaleString('th-TH') + ' ใบ', icon: 'view_carousel', tone: overLimit ? 'warning' : 'brand', note: overLimit ? `เกิน ${BANNER_VISIBLE_LIMIT} ใบ` : undefined },
    { label: 'ตั้งเวลาไว้', value: ordered.filter((b) => b.status === 'scheduled').length.toLocaleString('th-TH') + ' ใบ', icon: 'schedule_send' },
    { label: 'การมองเห็นรวมเดือนนี้', value: ordered.reduce((s, b) => s + b.impressions, 0).toLocaleString('th-TH'), icon: 'visibility' },
    { label: 'การกดรวมเดือนนี้', value: ordered.reduce((s, b) => s + b.taps, 0).toLocaleString('th-TH'), icon: 'touch_app', tone: 'success' },
  ];

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="แบนเนอร์หน้าแรก"
        count={ordered.length}
        end={
          <>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => {
                const b: Banner = {
                  id: `bn-${Date.now()}`, title: '', order: ordered.length + 1,
                  imageUrl: null, imageAlt: '', from: '2026-09-01', to: '2026-09-30',
                  status: 'draft', audience: { tiers: [] }, linkTo: '', impressions: 0, taps: 0,
                };
                setDraft((prev) => [...prev, b]);
                setEdit(b);
              }}
            >
              <Icon name="add" size={17} />
              เพิ่มแบนเนอร์
            </button>
            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              สถานะ
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className={inputCls}>
                <option value="all">ทั้งหมด</option>
                {(Object.keys(BANNER_STATUS_LABEL) as BannerStatus[]).map((s) => (
                  <option key={s} value={s}>{BANNER_STATUS_LABEL[s]}</option>
                ))}
              </select>
            </label>
          </>
        }
      />

      <SummaryStrip stats={stats} />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 gap-[var(--space-4)] overflow-y-auto p-[var(--space-4)]">
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-3)]">
            {overLimit && (
              <Callout tone="warning" title={`มีแบนเนอร์แสดงอยู่ ${liveCount} ใบ`}>
                จากอัตราการกดของใบท้าย ๆ ลูกค้าส่วนใหญ่เลื่อนไม่ถึงใบที่ {BANNER_VISIBLE_LIMIT + 1} —
                การเพิ่มใบใหม่ไม่ได้เพิ่มการมองเห็นรวม แต่ทำให้ใบที่ 1–{BANNER_VISIBLE_LIMIT} ถูกเห็นน้อยลง
                ถ้าต้องขึ้นใบใหม่ ให้หยุดใบที่กดน้อยที่สุดก่อน
              </Callout>
            )}
            {missingImage > 0 && (
              <Callout tone="error" title={`${missingImage} ใบยังไม่มีภาพแต่ตั้งสถานะให้แสดงแล้ว`}>
                แบนเนอร์ที่ไม่มีภาพจะเป็นช่องว่างบนหน้าแรก — อัปโหลดภาพ 16:9 หรือเปลี่ยนสถานะเป็นฉบับร่างก่อน
              </Callout>
            )}

            <FormSection
              title="ลำดับการแสดงบนหน้าแรก"
              requirement="FR-HOME-06"
              description="ใบที่ 1 คือใบที่ลูกค้าเห็นก่อนโดยไม่ต้องเลื่อน — เลื่อนขึ้นลงเพื่อจัดลำดับ ลำดับจะต่อเนื่องเสมอ"
            >
              <ul className="m-0 flex list-none flex-col gap-[var(--space-2)] p-0">
                {shown.map((b) => {
                  const rate = ctr(b);
                  const st = STATUS_STYLE[b.status];
                  const i = ordered.findIndex((x) => x.id === b.id);
                  const belowFold = b.status === 'live' && i + 1 > BANNER_VISIBLE_LIMIT;
                  return (
                    <li
                      key={b.id}
                      className="flex items-start gap-[var(--space-3)] rounded-[var(--radius-md)] border bg-[var(--surface-0)] p-[var(--space-25)]"
                      style={{ borderColor: belowFold ? 'var(--warning-border)' : 'var(--line-200)' }}
                    >
                      <span className="flex shrink-0 flex-col items-center gap-[var(--space-05)]">
                        <button
                          type="button"
                          aria-label={`เลื่อน ${b.title} ขึ้น`}
                          disabled={i === 0}
                          onClick={() => move(b.id, -1)}
                          className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        >
                          <Icon name="arrow_upward" size={17} />
                        </button>
                        <span className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'center' }}>
                          {b.order}
                        </span>
                        <button
                          type="button"
                          aria-label={`เลื่อน ${b.title} ลง`}
                          disabled={i === ordered.length - 1}
                          onClick={() => move(b.id, 1)}
                          className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        >
                          <Icon name="arrow_downward" size={17} />
                        </button>
                      </span>

                      {b.imageUrl ? (
                        <img src={b.imageUrl} alt="" className="h-[63px] w-[112px] shrink-0 rounded-[var(--radius-sm)] object-cover" />
                      ) : (
                        <span className="flex h-[63px] w-[112px] shrink-0 flex-col items-center justify-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-dashed border-[var(--line-300)] bg-[var(--surface-100)]">
                          <Icon name="add_photo_alternate" size={19} className="text-[var(--ink-600)]" />
                          <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ยังไม่มีภาพ</span>
                        </span>
                      )}

                      <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-05)]">
                        <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                          <span className="bo-truncate text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                            {b.title || '(ยังไม่ตั้งชื่อ)'}
                          </span>
                          <span
                            className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
                            style={{ background: st.bg, color: st.fg }}
                          >
                            <Icon name={st.icon} size={14} filled />
                            {BANNER_STATUS_LABEL[b.status]}
                          </span>
                          {belowFold && (
                            <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                              <Icon name="visibility_off" size={14} filled />
                              ลูกค้าส่วนใหญ่เลื่อนไม่ถึง
                            </span>
                          )}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-[var(--space-3)] text-[length:var(--text-table)] text-[var(--ink-700)]">
                          <span className="flex items-center gap-1">
                            <Icon name="event" size={15} />
                            <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(b.from)} – {formatThaiDate(b.to)}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="groups" size={15} />
                            {audienceText(b)}
                          </span>
                          {b.linkTo && (
                            <span className="flex items-center gap-1">
                              <Icon name="link" size={15} />
                              <span className="bo-truncate max-w-[180px]">{b.linkTo}</span>
                            </span>
                          )}
                        </span>
                        {b.impressions > 0 && (
                          <span className="flex flex-wrap items-center gap-x-[var(--space-3)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                            <span>เห็น <strong className="bo-num">{b.impressions.toLocaleString('th-TH')}</strong> ครั้ง</span>
                            <span>กด <strong className="bo-num">{b.taps.toLocaleString('th-TH')}</strong> ครั้ง</span>
                            {rate !== null && (
                              <span style={{ color: rate < 3 ? 'var(--warning-text)' : 'var(--success-text)' }}>
                                อัตราการกด <strong className="bo-num">{rate.toFixed(1)}%</strong>
                                {rate < 3 ? ' — ต่ำกว่าค่าเฉลี่ย' : ''}
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      <span className="flex shrink-0 items-center gap-[var(--space-1)]">
                        <select
                          value={b.status}
                          onChange={(e) => patch(b.id, { status: e.target.value as BannerStatus })}
                          className={inputCls}
                          aria-label={`สถานะของ ${b.title}`}
                        >
                          {(Object.keys(BANNER_STATUS_LABEL) as BannerStatus[]).map((s) => (
                            <option key={s} value={s}>{BANNER_STATUS_LABEL[s]}</option>
                          ))}
                        </select>
                        <button type="button" onClick={() => setEdit(b)} className={secondaryBtnCls}>แก้ไข</button>
                      </span>
                    </li>
                  );
                })}
              </ul>

              {shown.length === 0 && (
                <p className="m-0 py-[var(--space-4)] text-center text-[length:var(--text-form)] text-[var(--ink-600)]">
                  ไม่มีแบนเนอร์ในสถานะนี้
                </p>
              )}
            </FormSection>

            <p className="m-0 pb-[var(--space-4)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
              ตัวเลขการมองเห็นและการกดเป็นข้อมูลของเดือนปัจจุบัน ดูย้อนหลังได้ในรายงานแคมเปญ —
              การจัดลำดับและเปลี่ยนสถานะทุกครั้งถูกบันทึกในประวัติการแก้ไข
            </p>
          </div>

          <aside className="sticky top-0 flex shrink-0 flex-col gap-[var(--space-3)] self-start">
            <div className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-25)]">
              <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)]">
                <Icon name="visibility" size={17} className="text-[var(--blue-700)]" />
                ดูอย่างสมาชิก
                <select value={previewAs} onChange={(e) => setPreviewAs(e.target.value as typeof previewAs)} className={inputCls}>
                  <option value="all">ไม่กรองกลุ่ม (เห็นทุกใบ)</option>
                  {TIERS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                {previewAs === 'all'
                  ? `แสดงแบนเนอร์ที่สถานะ "แสดงอยู่" ทั้ง ${previewList.length} ใบ`
                  : `สมาชิก ${TIERS.find((t) => t.id === previewAs)?.name} เห็น ${previewList.length} ใบ จากทั้งหมด ${liveCount} ใบที่แสดงอยู่`}
              </span>
            </div>

            <PhoneFrame
              caption="พรีวิวหน้าแรกของแอป — แบนเนอร์เลื่อนแนวนอน ใบแรกคือใบที่เห็นก่อนโดยไม่ต้องเลื่อน"
              height={560}
            >
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)' }}>สวัสดีคุณ ณิชา</span>
                  <span style={{ fontSize: 16, lineHeight: 1.6, fontWeight: 600, color: 'var(--ink-900)' }}>
                    Gold Member — 1,850 คะแนน
                  </span>
                </div>

                {previewList.length === 0 ? (
                  <div
                    style={{
                      border: '1px dashed var(--line-300)', borderRadius: 12, padding: 24,
                      textAlign: 'center', fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)',
                    }}
                  >
                    ไม่มีแบนเนอร์ที่กลุ่มนี้เห็น — พื้นที่นี้จะหายไปจากหน้าแรก
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
                    {previewList.map((b, i) => (
                      <div key={b.id} style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {b.imageUrl ? (
                          <img
                            src={b.imageUrl}
                            alt={b.imageAlt || b.title}
                            style={{ width: 300, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12, display: 'block' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 300, aspectRatio: '16 / 9', borderRadius: 12,
                              background: 'var(--surface-100)', border: '1px dashed var(--line-300)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)', textAlign: 'center', padding: 12,
                            }}
                          >
                            ยังไม่มีภาพ — จะเป็นช่องว่างบนหน้าแรก
                          </div>
                        )}
                        <span style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-900)' }}>
                          {i + 1}. {b.title || '(ยังไม่ตั้งชื่อ)'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  {['แลกของรางวัล', 'สิทธิ์ของฉัน'].map((label) => (
                    <div
                      key={label}
                      style={{
                        flex: 1, minHeight: 44, borderRadius: 12, background: 'var(--surface-100)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)',
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </PhoneFrame>
          </aside>
        </div>

        <SaveBar
          changeCount={changes.length}
          changes={changes}
          onSave={() => setConfirmOpen(true)}
          onDiscard={() => setDraft(saved)}
          blockedReason={missingAlt ? 'มีภาพที่ยังไม่ได้กรอกข้อความแทนภาพ' : undefined}
          impact={<span>ลำดับและสถานะใหม่ขึ้นบนหน้าแรกของแอปทันทีที่บันทึก — ไม่ต้องรอรอบปล่อยเวอร์ชัน</span>}
        />
      </div>

      {/* --------------------------------------------------------- แก้ไขแบนเนอร์ */}
      <div>
        {edit && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`แก้ไขแบนเนอร์ ${edit.title || 'ใหม่'}`}
            className="fixed inset-0 z-[var(--z-drawer)] flex items-start justify-center overflow-y-auto p-[var(--space-6)]"
            style={{ background: 'var(--scrim)' }}
          >
            <div
              className="flex w-full max-w-[880px] flex-col gap-[var(--space-4)] rounded-[var(--radius-lg)] bg-[var(--surface-0)] p-[var(--space-5)]"
              style={{ boxShadow: 'var(--elevation-modal)' }}
            >
              <header className="flex items-start justify-between gap-[var(--space-3)]">
                <div className="flex flex-col gap-[var(--space-05)]">
                  <h2 className="m-0 text-[length:var(--text-page-title)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                    {saved.some((x) => x.id === edit.id) ? 'แก้ไขแบนเนอร์' : 'แบนเนอร์ใหม่'}
                  </h2>
                  <p className="m-0 text-[length:var(--text-form)] text-[var(--ink-700)]">ลำดับที่ {edit.order} จาก {ordered.length} ใบ</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEdit(null)}
                  aria-label="ปิด"
                  className="inline-flex min-h-[var(--hit-control)] min-w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                >
                  <Icon name="close" size={20} />
                </button>
              </header>

              <FormRow label="ชื่อแบนเนอร์" required help="ใช้อ้างอิงในรายงานและใต้ภาพในพรีวิว — ลูกค้าเห็นข้อความในภาพ ไม่ใช่ชื่อนี้">
                <input
                  data-autofocus
                  type="text"
                  value={edit.title}
                  onChange={(e) => { patch(edit.id, { title: e.target.value }); setEdit({ ...edit, title: e.target.value }); }}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น แลกคะแนนรับคูปองส่วนลด 300 บาท"
                />
              </FormRow>

              <ImageDropzone
                value={edit.imageUrl ? { url: edit.imageUrl, alt: edit.imageAlt } : null}
                onChange={(v: ImageValue | null) => {
                  const next = { imageUrl: v?.url ?? null, imageAlt: v?.alt ?? '' };
                  patch(edit.id, next);
                  setEdit({ ...edit, ...next });
                }}
                spec={IMAGE_SPEC}
                label="ภาพแบนเนอร์ 16:9"
                previewWidth={400}
              />

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="เริ่มแสดง">
                  <input
                    type="date"
                    value={edit.from}
                    onChange={(e) => { patch(edit.id, { from: e.target.value }); setEdit({ ...edit, from: e.target.value }); }}
                    className={`${inputCls} bo-num`}
                  />
                </FormRow>
                <FormRow label="แสดงถึง">
                  <input
                    type="date"
                    value={edit.to}
                    onChange={(e) => { patch(edit.id, { to: e.target.value }); setEdit({ ...edit, to: e.target.value }); }}
                    className={`${inputCls} bo-num`}
                  />
                </FormRow>
              </div>

              <FormRow label="กลุ่มผู้รับ" help="ไม่เลือกเลย = สมาชิกทุกระดับเห็นแบนเนอร์นี้">
                <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                  {TIERS.map((t) => {
                    const on = edit.audience.tiers.includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                          on ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => {
                            const tiers = e.target.checked
                              ? [...edit.audience.tiers, t.id]
                              : edit.audience.tiers.filter((x) => x !== t.id);
                            patch(edit.id, { audience: { ...edit.audience, tiers } });
                            setEdit({ ...edit, audience: { ...edit.audience, tiers } });
                          }}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                        {t.name}
                        <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{t.memberCount.toLocaleString('th-TH')} คน</span>
                      </label>
                    );
                  })}
                </span>
              </FormRow>

              <FormRow
                label="เงื่อนไขเพิ่มเติมของกลุ่มผู้รับ"
                help="ระบุเป็นข้อความในรอบนี้ — การเลือกกลุ่มผู้รับแบบละเอียดจะเปิดในเฟสถัดไป"
              >
                <input
                  type="text"
                  value={edit.audience.segment ?? ''}
                  onChange={(e) => {
                    const segment = e.target.value || undefined;
                    patch(edit.id, { audience: { ...edit.audience, segment } });
                    setEdit({ ...edit, audience: { ...edit.audience, segment } });
                  }}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น ยังไม่เคยสั่งซื้อ · กลุ่ม ปตท."
                />
              </FormRow>

              <FormRow label="ปลายทางเมื่อกด" help="เส้นทางในแอป เช่น /rewards หรือ /privileges/pv-01">
                <input
                  type="text"
                  value={edit.linkTo}
                  onChange={(e) => { patch(edit.id, { linkTo: e.target.value }); setEdit({ ...edit, linkTo: e.target.value }); }}
                  className={`${inputCls} bo-num w-full`}
                  placeholder="/rewards"
                />
              </FormRow>

              {edit.impressions > 0 && (
                <div className="grid grid-cols-3 gap-[var(--space-3)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
                  <Field label="เห็น">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{edit.impressions.toLocaleString('th-TH')} ครั้ง</span>
                  </Field>
                  <Field label="กด">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{edit.taps.toLocaleString('th-TH')} ครั้ง</span>
                  </Field>
                  <Field label="อัตราการกด" hint={(ctr(edit) ?? 0) < 3 ? 'ต่ำกว่าค่าเฉลี่ยของแบนเนอร์อื่น' : undefined}>
                    <span className="bo-num" style={{ textAlign: 'left' }}>{(ctr(edit) ?? 0).toFixed(1)}%</span>
                  </Field>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-[var(--space-2)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
                <button type="button" onClick={() => setEdit(null)} className={primaryBtnCls} disabled={!edit.title.trim()}>
                  เสร็จแล้ว
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraft((prev) => prev.filter((x) => x.id !== edit.id).map((b, k) => ({ ...b, order: k + 1 })));
                    setEdit(null);
                  }}
                  className={ghostBtnCls}
                  style={{ color: 'var(--error-text)' }}
                >
                  <Icon name="delete" size={16} />
                  ลบแบนเนอร์นี้
                </button>
                <span className="flex-1" />
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                  การแก้ไขจะถูกบันทึกเมื่อกด &quot;บันทึกการเปลี่ยนแปลง&quot; ที่แถบด้านล่าง
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone={liveCount > 0 ? 'danger' : 'default'}
        title="บันทึกแบนเนอร์หน้าแรก"
        description={
          <>
            <p className="m-0 mb-[var(--space-2)]">
              แก้ไป {changes.length.toLocaleString('th-TH')} รายการ — ลำดับและสถานะใหม่ขึ้นบนหน้าแรกของแอปทันที
            </p>
            <p className="m-0 font-[var(--weight-medium)] text-[var(--ink-900)]">
              จะมีแบนเนอร์แสดงอยู่ {liveCount} ใบ
              {overLimit ? ` — เกินจำนวนที่ลูกค้าเลื่อนดูจริง (${BANNER_VISIBLE_LIMIT} ใบ)` : ''}
            </p>
          </>
        }
        confirmLabel="บันทึกและขึ้นหน้าแรก"
        requireReason
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'จัดลำดับตามแผนแคมเปญเดือนนี้',
          'หยุดแบนเนอร์ที่แคมเปญสิ้นสุดแล้ว',
          'ขึ้นแบนเนอร์ใหม่ตามที่ฝ่ายการตลาดขอ',
        ]}
        onConfirm={doSave}
        onCancel={() => setConfirmOpen(false)}
      />

      {screenState === 'error' && (
        <div className="p-[var(--space-4)]">
          <Callout tone="error" title="โหลดรายการแบนเนอร์ไม่สำเร็จ">เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ลองใหม่อีกครั้ง</Callout>
        </div>
      )}
    </div>
  );
};

export default BannerScreen;

/* ============================================================================
   รองรับ:
   FR-HOME-06  ปรับเนื้อหา/แบนเนอร์บนหน้าแรกแบบ Dynamic โดย Admin และแสดงต่างกันตาม Segment
   FR-ADM-19   สร้าง/แก้ไข/เผยแพร่/หยุดแคมเปญได้เองโดยไม่ต้องเขียนโปรแกรม
   FR-ADM-25   กลุ่มผู้รับอ้างถึง Segment — หน้าจอ Segment เต็มรูปแบบอยู่ในกลุ่มถัดไป
   FR-ADM-37   การจัดลำดับและเปลี่ยนสถานะทุกครั้งเข้า Audit Log

   ประเด็นค้าง (⛔):
   — จำนวนแบนเนอร์ที่แสดงพร้อมกันได้จริง (ตอนนี้ใช้ 5 ใบเป็นเกณฑ์เตือน)
     ต้องยืนยันกับข้อมูลการเลื่อนจริงหลังเปิดใช้ 1 เดือน
   — การเลือกกลุ่มผู้รับในรอบนี้เลือกได้แค่ระดับสมาชิก ส่วนเงื่อนไขอื่นเป็นข้อความอิสระ
     การเลือก Segment จริงต้องรอหน้าจอ Segment (FR-ADM-25)
   — ยังไม่มีการตั้งเวลาแสดงเป็นชั่วโมง (เช่น แบนเนอร์ Flash Sale 18:00–21:00)
     ถ้าต้องมี ต้องเปลี่ยนช่องวันที่เป็นวันที่+เวลาทั้งระบบ
   — ภาพแบนเนอร์ไม่มีการรองรับหลายความละเอียด (1x/2x/3x) ต้องยืนยันกับทีมพัฒนา
     ว่าย่อภาพเดียวพอสำหรับจอความละเอียดสูงหรือไม่
   — ข้อความในภาพแบนเนอร์อ่านไม่ได้ด้วยโปรแกรมอ่านหน้าจอ — ข้อความแทนภาพจึงบังคับกรอก
     แต่ข้อความสำคัญควรอยู่นอกภาพ ซึ่งต้องแก้ที่ฝั่งแอป ไม่ใช่ที่หน้านี้
   ============================================================================ */
